import React, { memo, useMemo } from 'react'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { useThemeStore } from '@/stores/themeStore'
import { cn } from '@/lib/utils'
import { FALLBACK_META, NODE_META, edgeLabel, nodeKind, subtitleOf } from '@/lib/workflowNodeMeta'

/**
 * A workflow drawn exactly as the builder saved it, with nothing editable:
 * no dragging, no connecting, no selection. Used by the approval review
 * modals so an approver can look at the whole flow without leaving the
 * approvals screen (brief C2: "the modal must render the workflow read-only").
 *
 * Takes the raw nodes/edges from GET /workflows/{id} (or the campaign review
 * payload); it never fetches on its own.
 */

export interface RawWorkflowNode {
  id: string
  type: string
  position?: { x: number; y: number }
  data?: Record<string, any>
}

export interface RawWorkflowEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  type?: string
}

interface Props {
  nodes: RawWorkflowNode[]
  edges: RawWorkflowEdge[]
  /** Pixel height of the canvas box. */
  height?: number
  className?: string
}

const PlainNode = memo(function PlainNode({ data, type }: NodeProps) {
  const meta = NODE_META[type] ?? FALLBACK_META
  const { Icon } = meta
  const kind = nodeKind(type, data)
  const subtitle = subtitleOf(type, data || {})
  const handleCls = '!h-2 !w-2 !border-2 !border-card !bg-muted-foreground/40'
  return (
    <div className="relative min-w-[190px] rounded-2xl border border-border bg-card px-4 py-3 shadow-card">
      <Handle type="target" position={Position.Top} className={handleCls} />
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-muted/70 text-muted-foreground">
          <Icon className="h-4 w-4" weight="bold" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-emphasis uppercase tracking-wider text-muted-foreground">{kind}</p>
          <p className="truncate text-sm font-medium text-foreground">{data?.label || kind}</p>
        </div>
      </div>
      {subtitle && <p className="mt-2 truncate pl-[46px] text-xs text-muted-foreground">{subtitle}</p>}
      <Handle type="source" position={Position.Bottom} className={handleCls} />
    </div>
  )
})

// Every known type maps to the same neutral component; a stable reference
// keeps ReactFlow from re-registering node types on each render.
const READONLY_NODE_TYPES: NodeTypes = Object.fromEntries(
  Object.keys(NODE_META).map(t => [t, PlainNode]),
) as NodeTypes

const Inner: React.FC<Props> = ({ nodes, edges, height = 360, className }) => {
  const isDark = useThemeStore(s => s.actualTheme === 'dark')

  const rfNodes = useMemo<Node[]>(
    () => nodes.map(n => ({
      id: n.id,
      // Unknown types still render (ReactFlow falls back to its default node);
      // every catalog type gets the neutral card above.
      type: NODE_META[n.type] ? n.type : undefined,
      position: n.position || { x: 0, y: 0 },
      data: { ...(n.data || {}), label: n.data?.label || nodeKind(n.type, n.data) },
      draggable: false,
      connectable: false,
      selectable: false,
    })),
    [nodes],
  )

  const rfEdges = useMemo<Edge[]>(() => {
    const stroke = isDark ? '#3a4d70' : '#cbd5e1'
    return edges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: e.type || 'smoothstep',
      label: edgeLabel(e.sourceHandle),
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 6,
      labelStyle: { fontSize: 11, fontWeight: 590, fill: isDark ? '#e5edf7' : '#0f172a' },
      labelBgStyle: { fill: isDark ? '#0c1320' : '#ffffff', fillOpacity: 0.95 },
      animated: false,
      style: { stroke, strokeWidth: 1.75 },
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: stroke },
    }))
  }, [edges, isDark])

  if (nodes.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">This workflow has no steps yet.</p>
  }

  return (
    <div
      className={cn('overflow-hidden rounded-xl border border-border/50', className)}
      style={{ height }}
      data-testid="readonly-workflow-canvas"
    >
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={READONLY_NODE_TYPES}
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        elementsSelectable={false}
        edgesFocusable={false}
        zoomOnDoubleClick={false}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
        proOptions={{ hideAttribution: true }}
        className="bg-background"
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color={isDark ? '#2a2b30' : '#d8dce3'} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  )
}

const ReadOnlyWorkflowCanvas: React.FC<Props> = (props) => (
  <ReactFlowProvider>
    <Inner {...props} />
  </ReactFlowProvider>
)

export default ReadOnlyWorkflowCanvas
